/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `LogoMnetProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LogoMnetProps {
    /**
     * A string representing the CSS class to be applied to the LogoMnetIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#1D1D1B'
     */
    color1?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#008FD6'
     */
    color2?: string;
    /**
     * A number or string representing the height of the LogoMnetIcon element.
     *
     * @default 29
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LogoMnet'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LogoMnetIcon element.
     *
     * @default 74
     */
    width?: number | string;
}

/**
 * The `LogoMnet` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LogoMnetProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LogoMnetIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.color2    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LogoMnetIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LogoMnetIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LogoMnetComponent = <LogoMnet className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const LogoMnet = ({
    className = '',
    color1 = '#1D1D1B',
    color2 = '#008FD6',
    height = 29,
    ref,
    testId = 'LogoMnet',
    width = 74
}: LogoMnetProps) => (
    <svg
        ref={ref}
        aria-label="M-net Logo"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 74 29"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M39.6 12.56c0-1.3-.06-2.4-.1-3.37h2.73l.15 1.7h.08a4.7 4.7 0 0 1 3.98-1.96c2.18 0 4.42 1.28 4.42 4.86v6.72h-3.14v-6.4c0-1.62-.66-2.85-2.38-2.85a2.6 2.6 0 0 0-2.45 1.67q-.16.45-.15.9v6.68h-3.15zM56 15.65c.08 2.05 1.81 2.93 3.83 2.93q1.77.02 3.45-.51l.46 1.95q-2.1.76-4.34.72c-4.04 0-6.42-2.25-6.42-5.72 0-3.13 2.1-6.09 6.09-6.09 4.03 0 5.36 3.03 5.36 5.51q0 .6-.1 1.21zm5.47-2c.02-1.04-.49-2.76-2.58-2.76-1.94 0-2.76 1.6-2.89 2.76zm8.94-7.36v2.9h2.99v2.14h-3v5c0 1.39.4 2.09 1.61 2.09q.61.02 1.23-.12l.05 2.16q-1.11.33-2.28.3a3.8 3.8 0 0 1-2.7-.93c-.7-.65-1-1.7-1-3.18v-5.32h-1.77V9.19h1.77V7.08zM36.7 14.85a1.9 1.9 0 0 1-1.2 1.79 1.96 1.96 0 0 1-2.66-1.41 1.9 1.9 0 0 1 .83-2q.5-.31 1.08-.32a1.95 1.95 0 0 1 1.95 1.94" fill={color1} />
        <path d="M33.79 21.82c0 .02-.77-1.3-.78-1.27-1.92 2.57-3.98 3.5-4.69 3.57-.84.08-1.33-.94-1.4-1.66-.25-2.77 1.86-6.44 3.29-8.62l3.62-5.85c.37-.58 1.34-1.95 1.27-2.68-.05-.6-1.15-2.06-2.03-2.6l-.22-.1a1 1 0 0 0-.4-.03c-.63.06-2.44 1.83-2.86 2.27-4.26 4.2-6.56 6.87-10.53 11.11 1.23-2.42 5.38-9.17 5.13-11.86-.17-1.89-2.03-2.37-2.8-2.27C19.95 2 17.22 5 16.03 6.4l-3.78 4.56c.9-2.31 1.46-4.64 1.12-6.6-.36-2.08-1.23-4.2-3.53-3.8C6.84 1.06.4 8.45 0 11.07l.37 1.34c3.89-5.5 6.33-7.6 7.3-7.76.84-.14 1.4.84 1.52 1.55.48 2.74-1.34 6.56-2.59 8.85l-4.2 7.71c-.32.61-1.17 2.06-1.06 2.78q.1.56.42 1.03a2.4 2.4 0 0 0 2.01 1.04c.9.01 1.34-.22 1.9-1.11l5.53-8.71 3.74-5.48 1.69-2.35s.92-1.21 1.58-1.97c.5-.6 1.02-.38 1.07 0q0 .6-.27 1.17c-.98 2.51-2.07 4.62-4.96 9.39L11.94 22c-.48.81-1.26 2.45-1.16 3.47.1 1.05.86 3.15 2.23 3.02.88-.08 2.38-2.3 2.84-3.04 4.85-7.73 6.35-9.3 11.85-15.07l.65-.66-.3.47c-2.15 3.53-6.1 9.39-5.7 13.75.2 2.1 1.18 4.32 3.5 4.1 2.01-.18 6.34-3.44 7.94-6.22" fill={color2} />
    </svg>
);

LogoMnet.displayName = 'LogoMnet';